'use client'

import { useState } from 'react'
import { cocoonPrivateRoutes } from '@cocoonspace/shared/config/cocoon-private-routes'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@cocoonspace/ui/components/dropdown-menu'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useLingui } from '@lingui/react/macro'
import { signOut } from 'firebase/auth'
import {
	CalendarIcon,
	ChevronDownIcon,
	HelpCircleIcon,
	LogOutIcon,
} from 'lucide-react'
import { HomeIcon } from 'lucide-react'
import { SearchIcon } from 'lucide-react'
import { LinkIcon } from 'lucide-react'
import { UserRoundIcon } from 'lucide-react'
import { MenuIcon } from 'lucide-react'
import { LocalizedLink as Link } from '~/components/localized-link'
import { usePathname, useRouter } from '~/hooks/use-localized-navigation.hook'
import { useAnalytics } from 'use-analytics'

import { useLocale } from '~/hooks/use-locale.hook'
import { getLocalizedPath, stripLocaleFromPath } from '~/i18n/routing'
import { getAuthInstance, useAuth } from '~/providers/auth-provider'
import { useUserContext } from '~/providers/user-provider'
import { formatPersonName } from '~/utils/format-person-name'
import { UserAvatar } from '../user-avatar'
import { LangSelector } from './lang-selector'

const AuthMenuContent = ({ onSignOut }: { onSignOut?: () => void }) => {
	const { t } = useLingui()
	const router = useRouter()
	const locale = useLocale()
	const pathname = stripLocaleFromPath(usePathname() ?? '')
	const analytics = useAnalytics()

	const handleSignOut = () => {
		const auth = getAuthInstance()

		return signOut(auth).then(() => {
			const mustRedirect = cocoonPrivateRoutes.find(
				({ path, mode }) => path.test(pathname) && mode === 'redirect',
			)

			analytics.reset()
			onSignOut?.()

			if (mustRedirect) {
				router.push('/')
			}
		})
	}

	return (
		<>
			<DropdownMenuItem asChild>
				<Link href={getLocalizedPath('/dashboard', locale)}>
					<HomeIcon />
					{t`Accueil`}
				</Link>
			</DropdownMenuItem>

			<DropdownMenuItem asChild>
				<Link href={getLocalizedPath('/paris', locale)}>
					<SearchIcon />
					{t`Trouver un Cocoon`}
				</Link>
			</DropdownMenuItem>

			<DropdownMenuItem asChild>
				<Link href={getLocalizedPath('/events', locale)}>
					<CalendarIcon />
					{t`Mes événements`}
				</Link>
			</DropdownMenuItem>

			<DropdownMenuItem asChild>
				<Link href={getLocalizedPath('/scheduling', locale)}>
					<LinkIcon />
					{t`Prise de rendez-vous`}
				</Link>
			</DropdownMenuItem>

			<DropdownMenuItem asChild>
				<Link href={getLocalizedPath('/account', locale)}>
					<UserRoundIcon />
					{t`Mon compte`}
				</Link>
			</DropdownMenuItem>

			<DropdownMenuSeparator className='bg-border' />

			<DropdownMenuItem asChild>
				<Link
					href='https://support.cocoon-space.com'
					target='_blank'
				>
					<HelpCircleIcon />
					{t`Besoin d'aide ?`}
				</Link>
			</DropdownMenuItem>

			<DropdownMenuSeparator className='bg-border' />

			<DropdownMenuItem asChild>
				<LangSelector />
			</DropdownMenuItem>

			<DropdownMenuSeparator className='bg-border' />

			<DropdownMenuItem onClick={handleSignOut}>
				<LogOutIcon />
				{t`Se déconnecter`}
			</DropdownMenuItem>
		</>
	)
}

export const UserMenu = () => {
	const { userQuery } = useUserContext()
	const { user } = useAuth()

	const [isOpen, setIsOpen] = useState(false)

	const firstName = userQuery.data?.first_name
	const lastName = userQuery.data?.last_name
	const email = userQuery.data?.email ?? user?.email ?? undefined

	const displayName =
		formatPersonName(firstName, lastName) || user?.displayName || email || ''

	return (
		<DropdownMenu
			open={isOpen}
			onOpenChange={setIsOpen}
		>
			<DropdownMenuTrigger className='group relative flex items-center gap-2 rounded-full border p-1 leading-none'>
				<UserAvatar />

				<div className='bg-primary/80 absolute left-1 hidden size-[32px] items-center justify-center rounded-full !stroke-[3px] text-white group-hover:flex'>
					<MenuIcon className='text-primary-foreground !stroke-[3px]' />
				</div>

				<div className='hidden min-w-0 truncate overflow-hidden text-left md:block md:w-[150px]'>
					{displayName ? (
						<div className='truncate font-bold'>{displayName}</div>
					) : userQuery.isLoading ? (
						<div className='text-muted-foreground truncate font-bold'>…</div>
					) : null}

					{email ? <p className='truncate text-sm'>{email}</p> : null}
				</div>

				<ChevronDownIcon className='mr-2' />
			</DropdownMenuTrigger>

			<DropdownMenuContent
				sideOffset={15}
				alignOffset={15}
				className={cn(
					'max-md:[&_*]:text-2xl max-md:[&_[role="menuitem"]]:p-4 max-md:[&_a]:p-4 max-md:[&_button]:h-auto max-md:[&_button]:p-4 max-md:[&_svg]:size-5',
				)}
			>
				<AuthMenuContent onSignOut={() => setIsOpen(false)} />
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
